import { gambits } from '@/core/gambits';

import type { BaseLocationDto, GeoCoords } from '@/core/data/location/location.interface';
import { LocationType } from '@/core/data/location/location.type';
import {
  type CreateIntentDto,
  type CreateProposalDto,
  type SegmentProposal,
  type SegmentResult,
  type SparkIntent,
  type SparkIntentListItem,
  type SparkProposalInvitation,
  type SparkSegment,
  type UpdateIntentAvailabilityByDateDto,
  type UpdateIntentAvailabilityHorizonDto,
} from '@/core/data/spark/spark.interface';
import { SmsCategory } from '@/core/data/user/user.interface';
import { createLogger } from '@/core/helpers/logger.helper';
import type { IntentResultOptions } from '@/core/network/api/spark/spark.api';
import { firestore } from '@/core/network/firebase';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { mergeSparkResultsWithInvitedProposals, type SparkResultEntry } from '@/stores/utils/spark-results-priority';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import { SparkSlotId, type SparkPlayer } from '@/views/composables/models/main/Spark';
import { toWallDateTime } from '@/views/main/sparks/utils/spark-day';
import { format } from 'date-fns';
import { doc, onSnapshot, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useAuthStore } from './auth.store';
import { useCityStore } from './city.store';
import { useGeolocationStore } from './geolocation.store';
import { useSparkIntentStore } from './spark.intent';
import { useSparkProposalStore } from './spark.proposal';

const DEFAULT_SPARKABLE_DISTANCE_METERS = 50_000;
const MISSING_SETUP_INTENT_RESULTS_MESSAGE = 'Unable to show matches. Missing spots or time slots.';
const MISSING_SETUP_INTENT_RESULTS_ERROR_KEYS = new Set<string>([]);
const SPARK_ROUTE_DATE_REGEX = /^(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/;
const SPARKS_DIAG_QUERY = 'sparks_diag';

const logger = createLogger('SparkStore');

interface GambitFirestore {
  id: string;
  updatedAt: Timestamp;
  currentSubscribers?: number;
}

export type SparkGambitChange =
  | { kind: 'joined' | 'left' | 'accepted' | 'updated' | 'deleted' | 'declined' | 'chatRead' }
  | { kind: 'merged'; intoGambitId: string };

export type SparkGambitPatch = (gambit: GambitSearchResultDto) => GambitSearchResultDto;

type SparkGambitAttendee = NonNullable<GambitSearchResultDto['attendees']>[number];

export interface SparkIntentResultsSnapshot {
  intent: SparkIntent;
  intentResults: SegmentResult[];
  invitedProposals: SegmentProposal[];
  sparkResults: SparkResultEntry[];
  invitedChallenges: GambitSearchResultDto[];
  invitedConfirmedGambits: GambitSearchResultDto[];
  confirmedGambits: GambitSearchResultDto[];
  fetchedAt: string;
}

interface FetchIntentResultsSnapshotOptions {
  forceRefresh?: boolean;
  silent?: boolean;
  intentResultOptions?: IntentResultOptions;
}

const extendedAvailabilityCache = new Set<string>();
const extendAvailabilitiesInFlightByKey = new Map<string, Promise<boolean>>();

export const useSparkStore = defineStore('spark', () => {
  const route = useRoute();
  const getRouteAvailabilityDate = (): string | undefined => {
    if (typeof route.query.date !== 'string') {
      return undefined;
    }

    return SPARK_ROUTE_DATE_REGEX.test(route.query.date) ? route.query.date : undefined;
  };
  const isSparksDiagEnabled = (): boolean => {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return true;
    }

    const rawValue = route.query[SPARKS_DIAG_QUERY];

    if (Array.isArray(rawValue)) {
      return rawValue.includes('1') || rawValue.includes('true');
    }

    return rawValue === '1' || rawValue === 'true';
  };

  const authStore = useAuthStore();
  const { userDetails } = storeToRefs(authStore);
  const cityStore = useCityStore();
  const { city } = storeToRefs(cityStore);
  const geolocationStore = useGeolocationStore();
  const proposalStore = useSparkProposalStore();
  const { sparkProposal } = storeToRefs(proposalStore);

  const gambitId = ref<string | null>(null);
  const sparkGambit = ref<GambitDetailDto | null>(null);
  const isGambitLoading = ref<boolean>(false);
  const isGambitError = ref<boolean>(false);
  const isGambitNotFound = ref<boolean>(false);
  const unsubscribeFromGambit = ref<Unsubscribe | null>(null);

  const isInitialized = ref<boolean>(false);
  const isSubmitting = ref<boolean>(false);
  const isError = ref<boolean>(false);
  const isRetrying = ref<boolean>(false);
  const gambitConflictId = ref<string | null>(null);

  const players = ref<Array<SparkPlayer | null>>([null, null, null]);
  const slotId = ref<SparkSlotId | null>(null);

  const nearbyLocations = ref<BaseLocationDto[]>([]);
  const gpsLocations = ref<BaseLocationDto[]>([]);
  const chosenLocationIds = ref<string[]>([]);
  const shouldPreselectLocations = ref<boolean>(false);

  const chosenLocations = computed((): BaseLocationDto[] => {
    return nearbyLocations.value.filter((location) => {
      return chosenLocationIds.value.some((id) => {
        return id === location.id;
      });
    });
  });

  const intentStore = useSparkIntentStore();
  const { sparkIntent } = storeToRefs(intentStore);

  const intentDateString = ref<string | null>(null);
  const isIntentLoading = ref<boolean>(false);
  const isIntentError = ref<boolean>(false);
  const isIntentNotFound = ref<boolean>(false);

  const intents = ref<SparkIntent[]>([]);
  const intentListItems = ref<SparkIntentListItem[]>([]);
  const intentResults = ref<SegmentResult[]>([]);
  const invitedProposals = ref<SegmentProposal[]>([]);
  const respondInvitedProposalIds = ref<string[]>([]);
  let respondInvitationsFetchPromise: Promise<void> | null = null;
  const invitedChallenges = ref<GambitSearchResultDto[]>([]);
  const invitedConfirmedGambits = ref<GambitSearchResultDto[]>([]);
  const confirmedGambits = ref<GambitSearchResultDto[]>([]);
  const activeResultsIntentId = ref<string | null>(null);
  const isActiveIntentMissingLocations = ref<boolean>(false);
  const priorityProposalId = ref<string | null>(null);
  const priorityProposalIntentId = ref<string | null>(null);
  const gambitResults = ref<GambitSearchResultDto[]>([]);
  const myGambits = ref<GambitSearchResultDto[]>([]);
  const intentResultsSnapshotCache = new Map<string, SparkIntentResultsSnapshot>();

  // Setters

  function setPlayers(newPlayers: Array<SparkPlayer | null>) {
    players.value = newPlayers;
  }

  function setSlotId(newSlotId: SparkSlotId | null) {
    slotId.value = newSlotId;
  }

  // Load state

  const isLoadingIntents = ref<boolean>(false);
  const isLoadingMyGambits = ref<boolean>(false);
  const isLoadingGambitResults = ref<boolean>(false);
  const isLoadingAvailabilities = ref<boolean>(false);
  const isLoadingResults = ref<boolean>(false);
  const isLoadingIntentResults = ref<boolean>(false);

  const isLoading = computed(() => {
    if (
      isLoadingMyGambits.value ||
      isLoadingGambitResults.value ||
      isLoadingAvailabilities.value ||
      isLoadingResults.value ||
      isLoadingIntents.value ||
      isLoadingIntentResults.value
    ) {
      return true;
    }

    return false;
  });

  // Computed values: Spark state

  const sparkResults = computed(() => {
    const priorityProposal =
      priorityProposalId.value && sparkProposal.value?.id === priorityProposalId.value ? sparkProposal.value : null;

    return mergeSparkResultsWithInvitedProposals({
      intentResults: intentResults.value,
      invitedProposals: invitedProposals.value,
      invitedChallenges: invitedChallenges.value,
      invitedGambits: [],
      currentUserId: userDetails.value?.id,
      fallbackTimeZone: sparkIntent.value?.timeZone,
      currentIntentId: activeResultsIntentId.value,
      priorityProposalId: priorityProposalId.value,
      priorityProposalIntentId: priorityProposalIntentId.value,
      priorityProposal,
    });
  });

  function setPriorityProposalId(id: string | null, intentId?: string | null) {
    priorityProposalId.value = id;
    priorityProposalIntentId.value = id ? intentId || null : null;
  }

  function clearPriorityProposalId() {
    priorityProposalId.value = null;
    priorityProposalIntentId.value = null;
  }

  const cloneSparkResultEntries = (entries: SparkResultEntry[]): SparkResultEntry[] => {
    return entries.map((entry) => ({
      segment: entry.segment,
      proposal: entry.proposal,
    }));
  };

  const cloneSparkIntent = (intent: SparkIntent): SparkIntent => {
    return {
      ...intent,
      timeSpan: { ...intent.timeSpan },
      locationIds: [...(intent.locationIds || [])],
      locations: intent.locations ? [...intent.locations] : intent.locations,
      availabilities: intent.availabilities ? [...intent.availabilities] : intent.availabilities,
      confirmations: intent.confirmations ? [...intent.confirmations] : intent.confirmations,
    };
  };

  const cloneIntentResultsSnapshot = (snapshot: SparkIntentResultsSnapshot): SparkIntentResultsSnapshot => {
    return {
      ...snapshot,
      intent: cloneSparkIntent(snapshot.intent),
      intentResults: [...snapshot.intentResults],
      invitedProposals: [...snapshot.invitedProposals],
      invitedChallenges: [...snapshot.invitedChallenges],
      invitedConfirmedGambits: [...snapshot.invitedConfirmedGambits],
      confirmedGambits: [...snapshot.confirmedGambits],
      sparkResults: cloneSparkResultEntries(snapshot.sparkResults),
    };
  };

  const isMissingSetupIntentResultsError = (error?: Partial<ApiError> | null): boolean => {
    if (!error) {
      return false;
    }

    if (
      error.errorKey &&
      error.errorKey !== 'error_generic' &&
      MISSING_SETUP_INTENT_RESULTS_ERROR_KEYS.has(error.errorKey)
    ) {
      return true;
    }

    return error.code === 400 && error.message === MISSING_SETUP_INTENT_RESULTS_MESSAGE;
  };

  const buildEmptyIntentResultsSnapshot = (intent: SparkIntent): SparkIntentResultsSnapshot => {
    return {
      intent: cloneSparkIntent(intent),
      intentResults: [],
      invitedProposals: [],
      invitedChallenges: [],
      invitedConfirmedGambits: [],
      confirmedGambits: [],
      sparkResults: [],
      fetchedAt: new Date().toISOString(),
    };
  };

  function invalidateIntentResultsSnapshotCache(intentIds?: string[]) {
    if (!intentIds?.length) {
      intentResultsSnapshotCache.clear();
      return;
    }

    intentIds.forEach((intentId) => {
      intentResultsSnapshotCache.delete(intentId);
    });
  }

  const droppedGambitId = ref<string | null>(null);
  const gambitDeckRefreshId = ref<string | null>(null);
  const droppedProposalId = ref<string | null>(null);
  const mergedProposalGambitId = ref<string | null>(null);
  const droppedProposalIds = new Set<string>();

  function omitGambitFromLists<T extends { id: string }>(gambits: T[], gambitId: string): T[] {
    return gambits.filter((gambit) => gambit.id !== gambitId);
  }

  function dropGambitFromResults(gambitId: string) {
    invitedChallenges.value = omitGambitFromLists(invitedChallenges.value, gambitId);
    invitedConfirmedGambits.value = omitGambitFromLists(invitedConfirmedGambits.value, gambitId);
    confirmedGambits.value = omitGambitFromLists(confirmedGambits.value, gambitId);

    intentResultsSnapshotCache.forEach((snapshot, intentId) => {
      intentResultsSnapshotCache.set(intentId, {
        ...snapshot,
        invitedChallenges: omitGambitFromLists(snapshot.invitedChallenges, gambitId),
        invitedConfirmedGambits: omitGambitFromLists(snapshot.invitedConfirmedGambits, gambitId),
        confirmedGambits: omitGambitFromLists(snapshot.confirmedGambits, gambitId),
      });
    });

    droppedGambitId.value = gambitId;
  }

  function requestGambitDeckRefresh(gambitId: string) {
    invalidateIntentResultsSnapshotCache();
    gambitDeckRefreshId.value = gambitId;
  }

  function clearGambitDeckRefresh() {
    gambitDeckRefreshId.value = null;
  }

  const patchedGambit = ref<{ id: string; patch: SparkGambitPatch } | null>(null);

  function patchGambitInLists(gambits: GambitSearchResultDto[], gambitId: string, patch: SparkGambitPatch) {
    return gambits.map((gambit) => (gambit.id === gambitId ? patch(gambit) : gambit));
  }

  function patchGambitInResults(gambitId: string, patch: SparkGambitPatch) {
    invitedChallenges.value = patchGambitInLists(invitedChallenges.value, gambitId, patch);
    invitedConfirmedGambits.value = patchGambitInLists(invitedConfirmedGambits.value, gambitId, patch);
    confirmedGambits.value = patchGambitInLists(confirmedGambits.value, gambitId, patch);

    intentResultsSnapshotCache.forEach((snapshot, intentId) => {
      intentResultsSnapshotCache.set(intentId, {
        ...snapshot,
        invitedChallenges: patchGambitInLists(snapshot.invitedChallenges, gambitId, patch),
        invitedConfirmedGambits: patchGambitInLists(snapshot.invitedConfirmedGambits, gambitId, patch),
        confirmedGambits: patchGambitInLists(snapshot.confirmedGambits, gambitId, patch),
      });
    });

    patchedGambit.value = { id: gambitId, patch };
  }

  function buildMyAttendee(): SparkGambitAttendee | null {
    const me = userDetails.value;
    if (!me) {
      return null;
    }

    return {
      id: me.id,
      username: me.username,
      rating: me.rating ?? 0,
      ratingDisplayMode: me.ratingDisplayMode,
      profileImageBlobHash: me.profileImageBlobHash ?? null,
      firstName: '',
      lastName: '',
      emoji: '',
      chessSetCount: 0,
      isBlocked: false,
      isConfirmed: true,
      isOrganizer: false,
      initiatedHandshakes: [],
      receivedHandshakes: [],
    };
  }

  function withMyAttendance(isAttending: boolean): SparkGambitPatch {
    return (gambit) => {
      if (!!gambit.isAttending === isAttending) {
        return gambit;
      }

      const myId = userDetails.value?.id;
      const myAttendee = isAttending ? buildMyAttendee() : null;
      const others = gambit.attendees?.filter((attendee) => attendee?.id !== myId);

      return {
        ...gambit,
        isAttending,
        attendeeCount: Math.max(0, (gambit.attendeeCount || 0) + (isAttending ? 1 : -1)),
        attendees: others && myAttendee ? [...others, myAttendee] : others,
      };
    };
  }

  const markChatRead: SparkGambitPatch = (gambit) =>
    gambit.hasUnreadChatMessages ? { ...gambit, hasUnreadChatMessages: false } : gambit;

  function gambitChanged(gambitId: string, change: SparkGambitChange) {
    switch (change.kind) {
      case 'chatRead':
        patchGambitInResults(gambitId, markChatRead);
        return;
      case 'deleted':
      case 'declined':
        dropGambitFromResults(gambitId);
        return;
      case 'merged':
        dropGambitFromResults(gambitId);
        requestGambitDeckRefresh(change.intoGambitId);
        return;
      case 'joined':
      case 'accepted':
        patchGambitInResults(gambitId, withMyAttendance(true));
        requestGambitDeckRefresh(gambitId);
        return;
      case 'left':
        patchGambitInResults(gambitId, withMyAttendance(false));
        requestGambitDeckRefresh(gambitId);
        return;
      case 'updated':
        requestGambitDeckRefresh(gambitId);
        return;
    }
  }

  function omitProposalFromSegments(segments: SegmentResult[], proposalId: string): SegmentResult[] {
    return segments.map((segment) => ({
      ...segment,
      proposals: (segment.proposals || []).filter((proposal) => proposal.id !== proposalId),
    }));
  }

  function omitDroppedProposalsFromSnapshot(snapshot: SparkIntentResultsSnapshot): SparkIntentResultsSnapshot {
    if (!droppedProposalIds.size) {
      return snapshot;
    }

    let nextIntentResults = snapshot.intentResults;
    droppedProposalIds.forEach((proposalId) => {
      nextIntentResults = omitProposalFromSegments(nextIntentResults, proposalId);
    });

    return {
      ...snapshot,
      invitedProposals: snapshot.invitedProposals.filter((proposal) => !droppedProposalIds.has(proposal.id)),
      intentResults: nextIntentResults,
      sparkResults: snapshot.sparkResults.filter(
        (entry) => !entry.proposal?.id || !droppedProposalIds.has(entry.proposal.id),
      ),
    };
  }

  function dropProposalFromResults(proposalId: string) {
    droppedProposalIds.add(proposalId);
    invitedProposals.value = invitedProposals.value.filter((proposal) => proposal.id !== proposalId);
    intentResults.value = omitProposalFromSegments(intentResults.value, proposalId);

    intentResultsSnapshotCache.forEach((snapshot, intentId) => {
      intentResultsSnapshotCache.set(intentId, {
        ...snapshot,
        invitedProposals: snapshot.invitedProposals.filter((proposal) => proposal.id !== proposalId),
        intentResults: omitProposalFromSegments(snapshot.intentResults, proposalId),
        sparkResults: snapshot.sparkResults.filter((entry) => entry.proposal?.id !== proposalId),
      });
    });

    if (priorityProposalId.value === proposalId) {
      clearPriorityProposalId();
    }

    droppedProposalId.value = proposalId;
  }

  function mergeProposalIntoGambit(proposalId: string, gambitId: string) {
    dropProposalFromResults(proposalId);
    gambitDeckRefreshId.value = gambitId;
    mergedProposalGambitId.value = gambitId;
  }

  function replaceGambitLists(lists: {
    invitedChallenges: GambitSearchResultDto[];
    invitedConfirmedGambits: GambitSearchResultDto[];
    confirmedGambits: GambitSearchResultDto[];
  }) {
    invitedChallenges.value = [...lists.invitedChallenges];
    invitedConfirmedGambits.value = [...lists.invitedConfirmedGambits];
    confirmedGambits.value = [...lists.confirmedGambits];
  }

  const getProposalOrderFromSegments = (segments: SegmentResult[]): string[] => {
    return segments.flatMap((segment) => {
      return (segment.proposals || []).map((proposal) => proposal.id);
    });
  };

  const areSparkNotificationsEnabled = computed(() => {
    const phone = userDetails.value?.phone;
    const hasPhoneNumber = phone?.length;
    if (!hasPhoneNumber) return false;

    const details = userDetails.value;
    if (details?.notificationPreferences != null) {
      return details.notificationPreferences.sms?.[SmsCategory.SPARKS] === true;
    }

    const legacy = details?.enabledNotifications;
    return Array.isArray(legacy) && legacy.includes('sms.spark_activity');
  });

  const hasConfirmations = computed(() => {
    if (!sparkGambit.value?.attendees) return false;

    return sparkGambit.value?.attendees.some((a) => a?.isConfirmed);
  });

  const isLocationReady = computed(() => {
    if (!sparkGambit.value?.location) return false;

    return sparkGambit.value.location.type !== LocationType.CITY;
  });

  // Data fetching

  let didExist = false;

  const clear = () => {
    if (unsubscribeFromGambit.value !== null) {
      unsubscribeFromGambit.value();
      unsubscribeFromGambit.value = null;
    }

    isGambitLoading.value = false;
    isGambitError.value = false;
    isGambitNotFound.value = false;
    sparkGambit.value = null;
    // gambitResults.value = [];
    intentResults.value = [];
    invitedProposals.value = [];
    respondInvitedProposalIds.value = [];
    respondInvitationsFetchPromise = null;
    invitedChallenges.value = [];
    invitedConfirmedGambits.value = [];
    confirmedGambits.value = [];
    activeResultsIntentId.value = null;
    isActiveIntentMissingLocations.value = false;
    priorityProposalId.value = null;
    priorityProposalIntentId.value = null;
    gambitResults.value = [];
    invalidateIntentResultsSnapshotCache();
    myGambits.value = [];
    intentListItems.value = [];
    isInitialized.value = false;
  };

  const subscribeToGambit = (id: string) => {
    if (unsubscribeFromGambit.value !== null) {
      unsubscribeFromGambit.value();
      unsubscribeFromGambit.value = null;
    }

    didExist = false;

    const docRef = doc(firestore, 'gambits', id);

    unsubscribeFromGambit.value = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          logger.info(`Gambit ${id} Firestore doc found.`);
          didExist = true;
          const data = docSnap.data() as GambitFirestore;

          if (!sparkGambit.value?.updatedAt || data.updatedAt.toDate() > new Date(sparkGambit.value?.updatedAt)) {
            fetchGambit(docSnap.id);
          }
        } else if (didExist) {
          logger.info(`Gambit ${id} Firestore doc deleted.`);
          isGambitNotFound.value = true;
          sparkGambit.value = null;
        } else {
          logger.warn(`Gambit ${id} Firestore doc not found. Creating.`);
          gambits.gambitService.createFirestoreDoc(id);
        }
      },
      (error) => {
        logger.error(`Gambit ${id} Firestore snapshot error:`, error);
      },
    );
  };

  // Fetch SparkGambit

  const fetchGambit = async (id: string, location?: GeoCoords): Promise<Result<GambitDetailDto> | undefined> => {
    gambitId.value = id;
    isGambitLoading.value = true;
    isGambitError.value = false;
    isGambitNotFound.value = false;

    const isRefresh = sparkGambit.value?.id === id;

    const result = await gambits.gambitService.getGambit(id, location);
    isGambitLoading.value = false;

    if (result?.data) {
      sparkGambit.value = result.data;

      if (!isRefresh) {
        subscribeToGambit(sparkGambit.value.id);
      }
    } else if (result?.error?.code === 404) {
      isGambitNotFound.value = true;
      sparkGambit.value = null;
    } else {
      isGambitError.value = true;
    }

    return result;
  };

  // Fetch Spark Intent

  const fetchIntent = async (
    dateString: string,
    options?: { preserveCurrentIntent?: boolean },
  ): Promise<Result<SparkIntent> | undefined> => {
    intentDateString.value = dateString;

    isIntentLoading.value = true;
    isIntentError.value = false;
    isIntentNotFound.value = false;

    const isRefresh = sparkIntent.value?.date === dateString;

    const preserveCurrentIntent = options?.preserveCurrentIntent === true;

    if (!isRefresh && !preserveCurrentIntent) {
      intentStore.clearIntentStore();
    }

    const result = await gambits.sparkService.getIntentByDate(dateString);
    isIntentLoading.value = false;

    if (result?.data) {
      if (!preserveCurrentIntent || isRefresh) {
        intentStore.replaceIntent(result.data);
      }
    } else if (result?.error?.code === 404) {
      isIntentNotFound.value = true;
      if (!preserveCurrentIntent) {
        intentStore.clearIntentStore();
      }
    } else {
      isIntentError.value = true;
    }

    return result;
  };

  const upsertIntentListItem = (nextIntent: SparkIntent) => {
    intents.value = intents.value.some((intent) => intent.id === nextIntent.id)
      ? intents.value.map((intent) => (intent.id === nextIntent.id ? nextIntent : intent))
      : [...intents.value, nextIntent];
  };

  const mergeIntentListItemIntoIntent = (
    intentListItem: SparkIntentListItem,
    existingIntent?: SparkIntent,
  ): SparkIntent => {
    return {
      ...(existingIntent || {}),
      id: intentListItem.id,
      userId: intentListItem.userId,
      updatedAt: intentListItem.updatedAt,
      date: intentListItem.date,
      timeSpan: intentListItem.timeSpan,
      timeZone: intentListItem.timeZone,
      cityId: intentListItem.cityId,
      city: intentListItem.city,
      locationIds: intentListItem.locationIds,
      locations: intentListItem.locations || [],
      radiusMeters: intentListItem.radiusMeters,
      isDisabled: intentListItem.isDisabled,
      minStartTime: intentListItem.minStartTime,
      maxEndTime: intentListItem.maxEndTime,
      confirmedGambitWindows: intentListItem.confirmedGambitWindows,
    };
  };

  async function findOrCreateIntent(payload: CreateIntentDto): Promise<Result<SparkIntent>> {
    const dateString = String(payload.start).substring(0, 10);

    if (!dateString) {
      return {
        error: {
          code: 400,
          message: 'Could not resolve intent date.',
        },
      };
    }

    const fetchResult = await fetchIntent(dateString);

    if (fetchResult?.data) {
      upsertIntentListItem(fetchResult.data);
      return { data: fetchResult.data };
    }

    if (fetchResult?.error && fetchResult.error.code !== 404) {
      return fetchResult;
    }

    const createResult = await gambits.sparkService.createIntent(payload);

    if (createResult.data) {
      await intentStore.replaceIntent(createResult.data);
      upsertIntentListItem(createResult.data);
      invalidateIntentResultsSnapshotCache();
      extendedAvailabilityCache.clear();
      return createResult;
    }

    if (createResult.error?.code === 409) {
      const retryFetchResult = await fetchIntent(dateString);

      if (retryFetchResult?.data) {
        upsertIntentListItem(retryFetchResult.data);
        return { data: retryFetchResult.data };
      }

      if (retryFetchResult?.error) {
        return retryFetchResult;
      }
    }

    return createResult;
  }

  // Actions

  async function findMyGambits(start: string | Date, end: string | Date) {
    isLoadingMyGambits.value = true;
    const resp = await gambits.gambitService.findMyGambits(start, end);
    isLoadingMyGambits.value = false;

    if (resp.data) {
      myGambits.value = resp.data.filter((gambit) => gambit.isAttending);
    }
  }

  async function fetchIntents(options?: { refreshExisting?: boolean }) {
    // Keep signature compatibility for callers that still pass refreshExisting.
    void options?.refreshExisting;
    isLoadingIntents.value = true;

    try {
      const resp = await gambits.sparkService.getIntents();

      if (resp.data) {
        const latestIntentListItems = resp.data;
        intentListItems.value = latestIntentListItems;
        const existingIntentsById = new Map(intents.value.map((intent) => [intent.id, intent]));

        intents.value = latestIntentListItems.map((intentListItem) =>
          mergeIntentListItemIntoIntent(intentListItem, existingIntentsById.get(intentListItem.id)),
        );

        invalidateIntentResultsSnapshotCache();
      } else if (resp.error) {
        throw new Error('Something went wrong.');
      }

      isInitialized.value = true;
    } finally {
      isLoadingIntents.value = false;
    }
  }

  async function ensureDailyAvailabilitiesExtended(cityId?: string): Promise<boolean> {
    if (!cityId) {
      return false;
    }

    const todayKey = format(new Date(), 'yyyy-MM-dd');
    const cityDayKey = `${cityId}:${todayKey}`;

    if (extendedAvailabilityCache.has(cityDayKey)) {
      return false;
    }

    const inFlightRequest = extendAvailabilitiesInFlightByKey.get(cityDayKey);

    if (inFlightRequest) {
      return inFlightRequest;
    }

    const extendRequest = (async () => {
      const response = await gambits.sparkService.extendAvailabilities(cityId);
      const createdIntents = response.data;

      if (!createdIntents) {
        return false;
      }

      if (createdIntents.length > 0) {
        extendedAvailabilityCache.add(cityDayKey);
        return true;
      }

      const hasSparkableLocations =
        city.value?.id === cityId &&
        Array.isArray(city.value.sparkableLocationIds) &&
        city.value.sparkableLocationIds.length > 0;

      if (!hasSparkableLocations) {
        return false;
      }

      extendedAvailabilityCache.add(cityDayKey);
      return true;
    })().finally(() => {
      extendAvailabilitiesInFlightByKey.delete(cityDayKey);
    });

    extendAvailabilitiesInFlightByKey.set(cityDayKey, extendRequest);

    return extendRequest;
  }

  async function initializeAvailabilityHorizon(cityId?: string): Promise<{ ok: boolean; error?: string }> {
    if (!cityId) {
      return {
        ok: false,
        error: 'Missing city id.',
      };
    }

    const response = await gambits.sparkService.initializeAvailabilityHorizon(cityId);

    if (!response.data) {
      return {
        ok: false,
        error: response.error?.message || 'Could not initialize availability horizon.',
      };
    }

    return { ok: true };
  }

  async function setCurrentIntentById(intentId: string) {
    if (!intents.value) {
      isError.value = true;
      return;
    }

    const intent = intents.value.find((intent) => intent.id === intentId);

    if (!intent) {
      isError.value = true;
      return;
    }

    if (sparkIntent.value?.id === intent.id) {
      return;
    }

    intentStore.replaceIntent(intent);

    cityStore.setCity(intent.city);

    await nextTick();

    if (intent.cityId === city.value?.id) {
      chosenLocationIds.value = intent.locationIds;
    } else {
      chosenLocationIds.value = [];
    }
  }

  async function createCurrentIntent(payload: CreateIntentDto) {
    intentStore.clearIntentStore();
    isError.value = false;
    isSubmitting.value = true;

    const wallStart = toWallDateTime(payload.start);
    const wallEnd = toWallDateTime(payload.end);

    const response = await gambits.sparkService.createIntent({
      ...payload,
      start: wallStart,
      end: wallEnd,
    });

    if (response.error || !response.data) {
      isSubmitting.value = false;
      isError.value = true;

      return;
    }

    intentStore.replaceIntent(response.data);
    chosenLocationIds.value = response.data.locationIds;

    intents.value = [...intents.value, response.data];
    invalidateIntentResultsSnapshotCache();

    await authStore.fetchUserInfo(true);

    isSubmitting.value = false;
  }

  async function updateCurrentIntent(intentId: string, payload: CreateIntentDto) {
    if (!sparkIntent.value || !intents.value) {
      return;
    }

    isError.value = false;
    isSubmitting.value = true;

    const wallStart = toWallDateTime(payload.start);
    const wallEnd = toWallDateTime(payload.end);

    const response = await gambits.sparkService.updateIntent(intentId, { ...payload, start: wallStart, end: wallEnd });

    if (response.error || !response.data) {
      isSubmitting.value = false;
      isError.value = true;
      return;
    }

    intentStore.replaceIntent(response.data);

    chosenLocationIds.value = response.data.locationIds;

    intents.value = intents.value.map((intent) => {
      if (intent.id === intentId) {
        return response.data!;
      }

      return intent;
    });
    invalidateIntentResultsSnapshotCache();

    isSubmitting.value = false;
  }

  async function markProposalSuccessAsRead(proposalId: string): Promise<Result<SegmentProposal>> {
    const response = await gambits.sparkService.markProposalSuccessAsRead(proposalId);

    if (response.data) {
      intentListItems.value = intentListItems.value.map((intent) => ({
        ...intent,
        unreadSuccessfulProposals: intent.unreadSuccessfulProposals.filter((item) => item.proposalId !== proposalId),
      }));
      invalidateIntentResultsSnapshotCache();
    }

    return response;
  }

  async function updateIntentAvailabilityByDate(dateString: string, payload: UpdateIntentAvailabilityByDateDto) {
    const response = await gambits.sparkService.updateIntentAvailabilityByDate(dateString, payload);

    if (!response.data) {
      return response;
    }

    const updatedIntent = response.data;

    intents.value = intents.value.some((intent) => intent.id === updatedIntent.id)
      ? intents.value.map((intent) => {
          if (intent.id === updatedIntent.id) {
            return updatedIntent;
          }

          return intent;
        })
      : [...intents.value, updatedIntent];

    if (sparkIntent.value?.id === updatedIntent.id || sparkIntent.value?.date === updatedIntent.date) {
      await intentStore.replaceIntent(updatedIntent);
    }

    invalidateIntentResultsSnapshotCache();

    return response;
  }

  async function saveAvailabilityDays(
    updates: Array<{
      date: string;
      isEnabled: boolean;
      start: string;
      end: string;
    }>,
  ): Promise<{ ok: boolean; failedDates: string[] }> {
    const failedDates: string[] = [];

    for (const update of updates) {
      const response = await updateIntentAvailabilityByDate(update.date, {
        isEnabled: update.isEnabled,
        start: update.start,
        end: update.end,
      });

      if (!response.data) {
        failedDates.push(update.date);
      }
    }

    return {
      ok: failedDates.length === 0,
      failedDates,
    };
  }

  async function saveAvailabilityHorizon(
    payload: UpdateIntentAvailabilityHorizonDto,
  ): Promise<{ ok: boolean; error?: string }> {
    const response = await gambits.sparkService.updateIntentAvailabilityHorizon(payload);

    if (!response.data) {
      return {
        ok: false,
        error: response.error?.message || 'Could not save availability horizon.',
      };
    }

    for (const updatedIntent of response.data) {
      upsertIntentListItem(updatedIntent);
    }

    const newCity = response.data[0]?.city;
    if (newCity && newCity.id !== city.value?.id) {
      cityStore.setCity(newCity);
    }

    const activeUpdatedIntent = response.data.find(
      (updatedIntent) => updatedIntent.id === sparkIntent.value?.id || updatedIntent.date === sparkIntent.value?.date,
    );

    if (activeUpdatedIntent) {
      await intentStore.replaceIntent(activeUpdatedIntent);
      chosenLocationIds.value = activeUpdatedIntent.locationIds;
    }

    invalidateIntentResultsSnapshotCache();

    return { ok: true };
  }

  async function deleteIntent(intentId: string) {
    isError.value = false;
    isSubmitting.value = true;
    const resp = await gambits.sparkService.deleteIntent(intentId);

    if (resp.error) {
      isSubmitting.value = false;
      isError.value = true;
      return;
    }

    intents.value = intents.value.filter((intent) => intent.id !== intentId);
    invalidateIntentResultsSnapshotCache();

    isSubmitting.value = false;
  }

  const resolveIntentById = (intentId: string): SparkIntent | null => {
    if (sparkIntent.value?.id === intentId) {
      return sparkIntent.value;
    }

    return intents.value.find((intent) => intent.id === intentId) || null;
  };

  const resolveIntentByDate = async (dateString: string): Promise<SparkIntent | null> => {
    const existingIntent = intents.value.find((intent) => intent.date === dateString) || null;

    if (existingIntent) {
      return existingIntent;
    }

    const fetchResult = await fetchIntent(dateString, {
      preserveCurrentIntent: true,
    });

    if (!fetchResult?.data) {
      return null;
    }

    upsertIntentListItem(fetchResult.data);

    return fetchResult.data;
  };

  const fetchIntentResultsPayload = async (
    intentId: string,
    options?: IntentResultOptions,
    fetchOptions?: { silent?: boolean },
  ): Promise<{
    intent: SparkIntent;
    intentResults: SegmentResult[];
    invitedProposals: SegmentProposal[];
    invitedChallenges: GambitSearchResultDto[];
    invitedConfirmedGambits: GambitSearchResultDto[];
    confirmedGambits: GambitSearchResultDto[];
  }> => {
    const shouldToggleLoading = fetchOptions?.silent !== true;

    if (shouldToggleLoading) {
      isLoadingIntentResults.value = true;
    }

    try {
      const gpsLocation = geolocationStore.userLocation?.location;
      const gpsCoords = gpsLocation ? { lat: gpsLocation.lat, lng: gpsLocation.lng } : undefined;
      const prioritizedResp = await gambits.sparkService.findPrioritizedIntentResults(intentId, gpsCoords);

      if (prioritizedResp.error) {
        throw prioritizedResp.error;
      }

      if (!prioritizedResp.data || !prioritizedResp.data.intent) {
        throw new Error('No response.');
      }

      if (isSparksDiagEnabled()) {
        logger.info('[SparksDiag] intent results payload (prioritized endpoint)', {
          intentId,
          priorityProposalId: priorityProposalId.value,
          backendResultProposalOrder: getProposalOrderFromSegments(prioritizedResp.data.results),
          invitedProposalOrder: prioritizedResp.data.invitedProposals.map((proposal) => proposal.id),
        });
      }

      return {
        intent: prioritizedResp.data.intent,
        intentResults: prioritizedResp.data.results,
        invitedProposals: prioritizedResp.data.invitedProposals,
        invitedChallenges: prioritizedResp.data.invitedChallenges,
        invitedConfirmedGambits: prioritizedResp.data.invitedConfirmedGambits,
        confirmedGambits: prioritizedResp.data.confirmedGambits,
      };
    } finally {
      if (shouldToggleLoading) {
        isLoadingIntentResults.value = false;
      }
    }
  };

  const syncCanonicalIntentFromResultsPayload = async (intentFromPayload: SparkIntent): Promise<SparkIntent> => {
    upsertIntentListItem(intentFromPayload);

    if (sparkIntent.value?.id === intentFromPayload.id) {
      await intentStore.replaceIntent(intentFromPayload);
    }

    return intentFromPayload;
  };

  const applyIntentResultsSnapshot = (snapshot: SparkIntentResultsSnapshot, ownerIntentId?: string): void => {
    intentResults.value = [...snapshot.intentResults];
    invitedProposals.value = [...snapshot.invitedProposals];
    invitedChallenges.value = [...snapshot.invitedChallenges];
    invitedConfirmedGambits.value = [...snapshot.invitedConfirmedGambits];
    confirmedGambits.value = [...snapshot.confirmedGambits];
    activeResultsIntentId.value = ownerIntentId || snapshot.intent.id;
  };

  function applyActiveIntentResultsSnapshot(snapshot: SparkIntentResultsSnapshot, ownerIntentId?: string): void {
    applyIntentResultsSnapshot(cloneIntentResultsSnapshot(snapshot), ownerIntentId);
  }

  async function fetchIntentResultsSnapshot(
    intent: SparkIntent,
    options?: FetchIntentResultsSnapshotOptions,
  ): Promise<SparkIntentResultsSnapshot> {
    const forceRefresh = options?.forceRefresh === true;
    const cachedSnapshot = intentResultsSnapshotCache.get(intent.id);
    const canonicalInputIntent = resolveIntentById(intent.id) || intent;
    const isActiveIntentRequest = sparkIntent.value?.id === canonicalInputIntent.id;

    if (cachedSnapshot && !forceRefresh && cachedSnapshot.intent.updatedAt === canonicalInputIntent.updatedAt) {
      return cloneIntentResultsSnapshot(cachedSnapshot);
    }

    if (isActiveIntentRequest) {
      isActiveIntentMissingLocations.value = false;
    }

    try {
      const nextPayload = await fetchIntentResultsPayload(intent.id, options?.intentResultOptions, {
        silent: options?.silent,
      });
      const canonicalIntent = await syncCanonicalIntentFromResultsPayload(nextPayload.intent);
      const priorityProposal =
        priorityProposalId.value && sparkProposal.value?.id === priorityProposalId.value ? sparkProposal.value : null;
      const mergedSparkResults = mergeSparkResultsWithInvitedProposals({
        intentResults: nextPayload.intentResults,
        invitedProposals: nextPayload.invitedProposals,
        invitedChallenges: nextPayload.invitedChallenges,
        invitedGambits: [],
        currentUserId: userDetails.value?.id,
        fallbackTimeZone: canonicalIntent.timeZone,
        currentIntentId: canonicalIntent.id,
        priorityProposalId: priorityProposalId.value,
        priorityProposalIntentId: priorityProposalIntentId.value,
        priorityProposal,
      });
      const nextSnapshot = omitDroppedProposalsFromSnapshot({
        intent: cloneSparkIntent(canonicalIntent),
        intentResults: [...nextPayload.intentResults],
        invitedProposals: [...nextPayload.invitedProposals],
        invitedChallenges: [...nextPayload.invitedChallenges],
        invitedConfirmedGambits: [...nextPayload.invitedConfirmedGambits],
        confirmedGambits: [...nextPayload.confirmedGambits],
        sparkResults: cloneSparkResultEntries(mergedSparkResults),
        fetchedAt: new Date().toISOString(),
      });

      intentResultsSnapshotCache.set(canonicalIntent.id, nextSnapshot);

      if (isActiveIntentRequest) {
        isActiveIntentMissingLocations.value = false;
      }

      return cloneIntentResultsSnapshot(nextSnapshot);
    } catch (error) {
      const apiError = error as Partial<ApiError>;

      if (!isMissingSetupIntentResultsError(apiError)) {
        throw error;
      }

      const emptySnapshot = buildEmptyIntentResultsSnapshot(canonicalInputIntent);
      intentResultsSnapshotCache.set(canonicalInputIntent.id, emptySnapshot);

      if (isActiveIntentRequest) {
        isActiveIntentMissingLocations.value = true;
      }

      return cloneIntentResultsSnapshot(emptySnapshot);
    }
  }

  async function findIntentResults(intentId: string, options?: IntentResultOptions) {
    const payload = await fetchIntentResultsPayload(intentId, options);
    const canonicalIntent = await syncCanonicalIntentFromResultsPayload(payload.intent);
    intentResults.value = payload.intentResults;
    invitedProposals.value = payload.invitedProposals;
    invitedChallenges.value = payload.invitedChallenges;
    invitedConfirmedGambits.value = payload.invitedConfirmedGambits;
    confirmedGambits.value = payload.confirmedGambits;
    activeResultsIntentId.value = canonicalIntent.id;
  }

  async function ensureRespondInvitationsLoaded(): Promise<void> {
    if (respondInvitationsFetchPromise) return respondInvitationsFetchPromise;
    respondInvitationsFetchPromise = (async () => {
      const result = await gambits.sparkService.getRespondSparkInvitations();
      if (result.data) {
        respondInvitedProposalIds.value = result.data.map((d) => d.proposalId);
      }
    })();
    return respondInvitationsFetchPromise;
  }

  async function ensureIntentsForInvitationDates(): Promise<void> {
    const result = await gambits.sparkService.getRespondSparkInvitations();
    if (!result.data?.length) return;

    respondInvitedProposalIds.value = result.data.map((d) => d.proposalId);

    const existingDates = new Set(intents.value.map((intent) => intent.date));
    const missingDates = [...new Set(result.data.map((d) => d.date))].filter((date) => !existingDates.has(date));

    await Promise.all(missingDates.map((date) => resolveIntentByDate(date)));
  }

  function findCurrentUserInvitation(proposal: SegmentProposal): SparkProposalInvitation | undefined {
    const currentUserId = userDetails.value?.id;

    if (!currentUserId || !Array.isArray(proposal.invitations)) {
      return undefined;
    }

    return proposal.invitations.find((invitation) => invitation.userId === currentUserId);
  }

  function syncInvitedProposal(proposal: SegmentProposal) {
    if (!Array.isArray(proposal.invitations)) {
      return;
    }

    const existingIndex = invitedProposals.value.findIndex((entry) => entry.id === proposal.id);
    const invitation = findCurrentUserInvitation(proposal);
    const isActive =
      invitation &&
      invitation.withdrawn !== true &&
      (invitation.accepted === null || invitation.accepted === undefined || invitation.accepted === true);

    if (!isActive) {
      if (existingIndex < 0) {
        return;
      }

      invitedProposals.value = invitedProposals.value.filter((entry) => entry.id !== proposal.id);
      return;
    }

    if (existingIndex < 0) {
      invitedProposals.value = [...invitedProposals.value, proposal];
      return;
    }

    const nextInvitedProposals = [...invitedProposals.value];
    nextInvitedProposals.splice(existingIndex, 1, proposal);
    invitedProposals.value = nextInvitedProposals;
  }

  async function findIntentAndGambitResults(intentId: string, options?: IntentResultOptions) {
    const intent = resolveIntentById(intentId);

    if (!intent) {
      return;
    }

    const snapshot = await fetchIntentResultsSnapshot(intent, {
      forceRefresh: true,
      silent: false,
      intentResultOptions: options,
    });

    applyIntentResultsSnapshot(snapshot, snapshot.intent.id);
  }

  async function createProposal(payload: CreateProposalDto) {
    if (!sparkIntent.value || !intentResults.value) return;

    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.createProposal(payload);

    if (!response.data) {
      if (response.error?.gambitId) {
        gambitConflictId.value = response.error.gambitId;
        isSubmitting.value = false;
        return;
      }

      isError.value = true;
      isSubmitting.value = false;

      response.error?.message && useToast().error(response.error.message);
      return;
    }

    gambitConflictId.value = null;

    replaceProposalInResults(response.data);
    invalidateIntentResultsSnapshotCache();

    isSubmitting.value = false;

    return response.data;
  }

  function replaceProposalInResults(proposal: SegmentProposal) {
    const proposalStartAt = new Date(proposal.start).getTime();
    let didMutate = false;

    intentResults.value = intentResults.value.map((result) => {
      const proposals = result.proposals || [];
      const existing = proposals.find((entry) => entry.id === proposal.id);

      if (existing) {
        didMutate = true;
        return {
          ...result,
          proposals: proposals.map((entry) => (entry.id === proposal.id ? proposal : entry)),
        };
      }

      if (didMutate) {
        return result;
      }

      const segmentStartAt = new Date(result.start).getTime();
      const segmentEndAt = new Date(result.end).getTime();
      const isSameLocation = proposal.location?.id === result.location.id;
      const isStartInSegment =
        Number.isFinite(proposalStartAt) && proposalStartAt >= segmentStartAt && proposalStartAt < segmentEndAt;

      if (isSameLocation && isStartInSegment) {
        didMutate = true;
        return {
          ...result,
          proposals: [...proposals, proposal],
        };
      }

      return result;
    });

    syncInvitedProposal(proposal);
  }

  async function createProposalInvitations(proposalId: string, userIds: string[]) {
    if (!userIds.length) {
      return;
    }

    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.createInvitations(proposalId, userIds);

    if (response.error) {
      isError.value = true;
      isSubmitting.value = false;
      return response;
    }

    const refreshedProposal = await proposalStore.fetchProposal(proposalId);

    if (refreshedProposal?.data) {
      replaceProposalInResults(refreshedProposal.data);
    }

    invalidateIntentResultsSnapshotCache();

    isSubmitting.value = false;

    return response;
  }

  async function acceptProposalInvitation(proposalId: string, chessSetCount?: number) {
    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.acceptInvitation(proposalId, chessSetCount);

    if (!response.data) {
      isError.value = true;
      isSubmitting.value = false;
      return response;
    }

    replaceProposalInResults(response.data);
    invalidateIntentResultsSnapshotCache();
    isSubmitting.value = false;

    return response;
  }

  async function declineProposalInvitation(proposalId: string) {
    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.declineInvitation(proposalId);

    if (response.error) {
      isError.value = true;
    }

    const refreshedProposal = await proposalStore.fetchProposal(proposalId);

    if (refreshedProposal?.data) {
      replaceProposalInResults(refreshedProposal.data);
    }

    if (!response.error) {
      invalidateIntentResultsSnapshotCache();
    }

    isSubmitting.value = false;

    return response;
  }

  async function withdrawProposalInvitation(proposalId: string) {
    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.withdrawInvitation(proposalId);

    if (response.error) {
      isError.value = true;
    }

    const refreshedProposal = await proposalStore.fetchProposal(proposalId);

    if (refreshedProposal?.data) {
      replaceProposalInResults(refreshedProposal.data);
    }

    if (!response.error) {
      invalidateIntentResultsSnapshotCache();
    }

    isSubmitting.value = false;

    return response;
  }

  async function joinProposal(proposalId: string, segment: SparkSegment) {
    if (!sparkIntent.value || !intentResults.value) return;

    const { locationId, timeSpan } = segment;
    const { start, end } = timeSpan;

    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.createConfirmation(proposalId, sparkIntent.value.id);

    if (!response.data) {
      isError.value = true;
      isSubmitting.value = false;

      response.error?.message && useToast().error(response.error.message);
      return;
    }

    intentResults.value = intentResults.value.map((result) => {
      if (result.location.id === locationId && result.start === start && result.end === end) {
        const existing = result.proposals.find((p) => p.id === response.data!.id);
        const updatedProposals = existing
          ? result.proposals.map((p) => (p.id === response.data!.id ? response.data! : p))
          : [...result.proposals, response.data!];
        return { ...result, proposals: updatedProposals };
      }

      return result;
    });

    syncInvitedProposal(response.data);
    invalidateIntentResultsSnapshotCache();
    isSubmitting.value = false;

    return response.data;
  }

  async function leaveProposal(proposalId: string, segment: SegmentResult): Promise<boolean> {
    if (!sparkIntent.value || !intentResults.value) return false;

    const { location, start, end } = segment;

    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.deleteConfirmation(proposalId);

    if (response.error) {
      isError.value = true;
      isSubmitting.value = false;
      return false;
    }

    const updatedProposal = response.data;

    intentResults.value = intentResults.value.map((result) => {
      if (result.location.id === location.id && result.start === start && result.end === end) {
        if (!updatedProposal) {
          return { ...result, proposals: result.proposals.filter((p) => p.id !== proposalId) };
        }

        const updatedProposals = result.proposals.map((p) => (p.id === proposalId ? updatedProposal : p));
        return { ...result, proposals: updatedProposals };
      }

      return result;
    });

    if (updatedProposal) {
      syncInvitedProposal(updatedProposal);
    }

    invalidateIntentResultsSnapshotCache();
    isSubmitting.value = false;
    return true;
  }

  async function updateConfirmationChessSetCount(proposalId: string, chessSetCount: number) {
    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.updateConfirmationChessSetCount(proposalId, chessSetCount);

    if (!response.data) {
      isError.value = true;
      isSubmitting.value = false;
      response.error?.message && useToast().error(response.error.message);
      return response;
    }

    replaceProposalInResults(response.data);
    invalidateIntentResultsSnapshotCache();
    isSubmitting.value = false;

    return response;
  }

  async function meetAnyways(proposalId: string) {
    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.meetAnyways(proposalId);

    if (!response.data) {
      isError.value = true;
      isSubmitting.value = false;
      return;
    }

    replaceProposalInResults(response.data);
    proposalStore.applyLiveProposal(response.data);
    isSubmitting.value = false;

    return response.data;
  }

  async function undoMeetAnyways(proposalId: string) {
    isError.value = false;
    isSubmitting.value = true;

    const response = await gambits.sparkService.undoMeetAnyways(proposalId);

    if (!response.data) {
      isError.value = true;
      isSubmitting.value = false;
      return;
    }

    replaceProposalInResults(response.data);
    proposalStore.applyLiveProposal(response.data);
    isSubmitting.value = false;

    return response.data;
  }

  async function reloadSparkableLocations(): Promise<boolean> {
    const cityCoords = city.value?.coordinates;
    if (!cityCoords) {
      return false;
    }

    const response = await gambits.locationService.getSparkableLocationsByCoordinates(cityCoords, {
      distance: DEFAULT_SPARKABLE_DISTANCE_METERS,
      perPage: 1024,
      availabilityDate: getRouteAvailabilityDate(),
    });

    if (!response.data) {
      return false;
    }

    nearbyLocations.value = response.data;

    await nextTick();

    if (shouldPreselectLocations.value) {
      const hasVettedSpots = nearbyLocations.value.some((loc) => loc.isVetted);

      chosenLocationIds.value = nearbyLocations.value
        .filter((loc) => {
          if (typeof loc.sparkAvailabilityCount === 'number' && loc.sparkAvailabilityCount > 0) return true;
          if (hasVettedSpots) return loc.isVetted;
          return true;
        })
        .map((loc) => loc.id);

      shouldPreselectLocations.value = false;
    }

    return true;
  }

  async function reloadGpsLocations(): Promise<boolean> {
    const gpsCoords = geolocationStore.userLocation?.location;
    if (!gpsCoords) {
      gpsLocations.value = [];
      return false;
    }

    const cityCoords = city.value?.coordinates;
    if (cityCoords) {
      const dlat = Math.abs(gpsCoords.lat - cityCoords.lat);
      const dlng = Math.abs(gpsCoords.lng - cityCoords.lng);
      if (dlat < 0.1 && dlng < 0.1) {
        gpsLocations.value = [];
        return false;
      }
    }

    const response = await gambits.locationService.getSparkableLocationsByCoordinates(gpsCoords, {
      distance: DEFAULT_SPARKABLE_DISTANCE_METERS,
      perPage: 1024,
      availabilityDate: getRouteAvailabilityDate(),
    });

    gpsLocations.value = response.data || [];
    return true;
  }

  // Side-effects

  watch(
    () => city.value,
    async (newCity, oldCity) => {
      if (!newCity || newCity.id === oldCity?.id) return;

      shouldPreselectLocations.value = true;
      await reloadSparkableLocations();
      await reloadGpsLocations();
    },
    { immediate: true },
  );

  watch(
    sparkProposal,
    (newProposal) => {
      if (!newProposal) return;
      replaceProposalInResults(newProposal);
    },
    { immediate: true },
  );

  watch(
    [sparkResults, priorityProposalId],
    ([resultEntries, linkedPriorityProposalId]) => {
      if (!isSparksDiagEnabled()) {
        return;
      }

      const mergedProposalOrder = resultEntries
        .map((entry) => entry.proposal?.id)
        .filter((proposalId): proposalId is string => Boolean(proposalId));
      const linkedProposalIndex = linkedPriorityProposalId ? mergedProposalOrder.indexOf(linkedPriorityProposalId) : -1;

      logger.info('[SparksDiag] merged spark results order', {
        linkedPriorityProposalId,
        linkedProposalIndex,
        mergedProposalOrder,
      });
    },
    { immediate: false },
  );

  const refreshResults = async () => {
    clear();
    clearPriorityProposalId();
    fetchIntents({ refreshExisting: true });
  };

  return {
    players,
    setPlayers,
    slotId,
    setSlotId,
    isRetrying,
    isLoading,
    isLoadingIntents,
    isLoadingIntentResults,
    isLoadingGambitResults,
    isSubmitting,
    isError,
    sparkGambit,
    areSparkNotificationsEnabled,
    isLocationReady,
    hasConfirmations,

    isInitialized,
    clear,
    refreshResults,
    unsubscribeFromGambit,
    fetchGambit,
    isGambitNotFound,
    gambitId,
    fetchIntents,
    reloadSparkableLocations,
    reloadGpsLocations,
    ensureDailyAvailabilitiesExtended,
    initializeAvailabilityHorizon,

    fetchIntent,
    resolveIntentByDate,
    findOrCreateIntent,
    createCurrentIntent,
    updateCurrentIntent,
    markProposalSuccessAsRead,
    updateIntentAvailabilityByDate,
    saveAvailabilityDays,
    saveAvailabilityHorizon,
    deleteIntent,
    setCurrentIntentById,
    isIntentLoading,
    isIntentNotFound,
    isIntentError,

    nearbyLocations,
    gpsLocations,
    chosenLocationIds,
    chosenLocations,

    shouldPreselectLocations,

    myGambits,
    findMyGambits,

    gambitResults,
    intentResults,
    invitedProposals,
    respondInvitedProposalIds,
    ensureRespondInvitationsLoaded,
    ensureIntentsForInvitationDates,
    invitedChallenges,
    invitedConfirmedGambits,
    confirmedGambits,
    droppedGambitId,
    gambitDeckRefreshId,
    droppedProposalId,
    mergedProposalGambitId,
    dropGambitFromResults,
    requestGambitDeckRefresh,
    clearGambitDeckRefresh,
    patchedGambit,
    gambitChanged,
    dropProposalFromResults,
    mergeProposalIntoGambit,
    replaceGambitLists,
    activeResultsIntentId,
    isActiveIntentMissingLocations,
    sparkResults,
    setPriorityProposalId,
    clearPriorityProposalId,

    findIntentResults,
    findIntentAndGambitResults,
    fetchIntentResultsSnapshot,
    applyActiveIntentResultsSnapshot,
    invalidateIntentResultsSnapshotCache,
    intents,
    intentListItems,
    createProposal,
    gambitConflictId,
    createProposalInvitations,
    acceptProposalInvitation,
    declineProposalInvitation,
    withdrawProposalInvitation,
    joinProposal,
    leaveProposal,
    updateConfirmationChessSetCount,
    meetAnyways,
    undoMeetAnyways,
    replaceProposalInResults,
  };
});
